// Browser thermal-printer receipt rendering + printing.
// Renders an order as a narrow thermal receipt into a hidden iframe and calls
// print(). Works with any printer installed on the KDS device (via the OS/
// browser print path). For fully silent printing, run the KDS device's browser
// in kiosk-printing mode with the thermal printer set as the default printer.

const gbp = (n) => `£${(Number(n) || 0).toFixed(2)}`;
const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

export function buildReceiptHtml(order, cfg = {}, restaurant = {}) {
  const width = cfg.paper_width === "58" ? "54mm" : "76mm";
  const header = cfg.header || restaurant.name || "";
  const footer = cfg.footer || "";
  const when = order.created_at ? new Date(order.created_at).toLocaleString() : new Date().toLocaleString();
  const declared = order.declared_allergens || [];
  const allergyNote = order.allergy_dietary_note || order.customer_allergy_note;

  const lines = (order.items || []).map((it) => {
    const opts = (it.options_snapshot || []).map((o) => (o.name && o.value ? `${o.name}: ${o.value}` : (o.value || o.name))).filter(Boolean).join(", ");
    const itemAll = (it.allergens_snapshot || []).map((a) => a.name || a).filter(Boolean);
    const matched = itemAll.filter((a) => declared.includes(a));
    return `
      <div class="row"><span class="qty">${it.quantity}x</span><span class="nm">${esc(it.item_name_snapshot)}</span><span class="pr">${gbp(it.total_price)}</span></div>
      ${opts ? `<div class="sub">${esc(opts)}</div>` : ""}
      ${it.notes ? `<div class="sub note">** ${esc(it.notes)}</div>` : ""}
      ${matched.length ? `<div class="sub alg">! ALLERGEN: ${esc(matched.join(", "))}</div>` : ""}`;
  }).join("");

  const tableLine = order.order_type === "DINE_IN" && (order.table_ids?.length || order.table_id)
    ? `<div class="meta">Dine-in</div>` : "";

  return `<!doctype html><html><head><meta charset="utf-8"><style>
    @page { size: ${width} auto; margin: 0; }
    * { box-sizing: border-box; }
    body { width: ${width}; margin: 0; padding: 4mm 3mm; font-family: 'Courier New', monospace; color: #000; }
    .center { text-align: center; }
    .hdr { font-size: 15px; font-weight: 700; }
    .ordno { font-size: 20px; font-weight: 700; margin: 4px 0; }
    .meta { font-size: 12px; }
    hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
    .row { display: flex; font-size: 14px; font-weight: 700; margin-top: 3px; }
    .qty { width: 26px; }
    .nm { flex: 1; }
    .pr { text-align: right; }
    .sub { font-size: 12px; padding-left: 26px; }
    .note { font-weight: 700; }
    .alg { font-weight: 700; text-transform: uppercase; }
    .alert { border: 2px solid #000; padding: 4px; font-size: 13px; font-weight: 700; margin: 6px 0; }
    .total { display: flex; justify-content: space-between; font-size: 16px; font-weight: 700; margin-top: 6px; }
    .ftr { font-size: 11px; margin-top: 8px; }
  </style></head><body>
    ${header ? `<div class="center hdr">${esc(header)}</div>` : ""}
    <div class="center ordno">${esc(order.order_number || "")}</div>
    <div class="center meta">${esc(pretty(order.order_type))}${order.fulfillment_type ? " · " + esc(pretty(order.fulfillment_type)) : ""}</div>
    ${tableLine}
    <div class="center meta">${esc(when)}</div>
    ${order.customer_name ? `<div class="meta">Customer: ${esc(order.customer_name)}${order.customer_phone ? " (" + esc(order.customer_phone) + ")" : ""}</div>` : ""}
    <hr/>
    ${lines}
    <hr/>
    <div class="total"><span>TOTAL</span><span>${gbp(order.total)}</span></div>
    ${declared.length ? `<div class="alert">CUSTOMER ALLERGIC TO: ${esc(declared.join(", "))}</div>` : ""}
    ${allergyNote ? `<div class="alert">ALLERGY NOTE: ${esc(allergyNote)}</div>` : ""}
    ${order.special_notes ? `<div class="meta">Note: ${esc(order.special_notes)}</div>` : ""}
    ${footer ? `<div class="center ftr">${esc(footer)}</div>` : ""}
  </body></html>`;
}

function pretty(s) { return String(s || "").replaceAll("_", " "); }

export function printReceipt(order, cfg = {}, restaurant = {}) {
  const html = buildReceiptHtml(order, cfg, restaurant);
  let frame = document.getElementById("dineflo-print-frame");
  if (!frame) {
    frame = document.createElement("iframe");
    frame.id = "dineflo-print-frame";
    frame.style.position = "fixed";
    frame.style.right = "0";
    frame.style.bottom = "0";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.border = "0";
    document.body.appendChild(frame);
  }
  const doc = frame.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  const copies = Math.max(1, Number(cfg.copies) || 1);
  const fire = () => {
    for (let i = 0; i < copies; i++) {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    }
  };
  // give the iframe a moment to render before printing
  setTimeout(fire, 300);
}
